import React, { FC, useCallback, useEffect, useState } from 'react';
import { WebUxPage, WebUxPageContent, WebUxPageItem } from '../webux/webux-layouts/WebUxPage';
import { Button, message, Modal, Space } from 'antd';
import { QueryBuilder } from '../../components/molecules/QueryBuilder/QueryBuilder';
import { DataTable, DataTablePagination } from '../../components/molecules/DataTable/DataTable';
import { useConnection } from '../../connection/Application';
import { InfoView, InfoViewItem } from '../../components/info-view/InfoView';
import { ExclamationCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { FlexBox } from '../../components/atoms/FlexBox';
import { FormModal, useFormModal } from '../../components/form-modal/FormModal';
import { PerformanceCycle } from '../../entity/PerformanceCycle';
import Form from '../../form/Form';
import { PageHeader } from '@ant-design/pro-layout';

const columns = [
  {
    title: 'Name',
    dataIndex: 'name',
    key: 'name',
  },
  {
    title: 'Start Date',
    dataIndex: 'startDate',
    key: 'startDate',
  },
  {
    title: 'End Date',
    dataIndex: 'endDate',
    key: 'endDate',
  },
  {
    title: 'Status',
    dataIndex: 'status',
    key: 'status',
  },
];

const notificationColumns = [
  {
    title: 'Title',
    dataIndex: 'title',
    key: 'title',
  },
  {
    title: 'Notification',
    dataIndex: 'notification',
    key: 'notification',
  },
  {
    title: 'Date',
    dataIndex: 'date',
    key: 'date',
  },
  {
    title: 'Status',
    dataIndex: 'status',
    key: 'status',
  },
];

const mapCycle = (item: PerformanceCycle) => ({
  ...item,
  key: item.id,
  duration: [item.startDate, item.endDate],
  notification: item.notification,
  teamGoalAvailableForLead: item.teamGoalAvailableForLead ?? false,
  teamSelfEvaluation: item.teamSelfEvaluation ?? false,
  icrObjection: item.icrObjection ?? false,
});

const schema = [
  {
    id: 'name',
    name: 'Name',
    type: 'string',
    options: {
      rules: [{ required: true, whitespace: true, message: 'Name required' }],
    },
  },
  {
    id: 'duration',
    name: 'Duration',
    type: 'dateRange',
    options: {
      rules: [{ required: true, message: 'Duration required' }],
    },
    layout: { span: 24 },
    props: { showTime: false },
  },
  {
    id: 'status',
    name: 'Status',
    type: 'select',
    props: { values: ['Active', 'Completed', 'Non Active', 'Cancelled'] },
  },
  {
    id: 'teamGoalAvailableForLead',
    name: 'Team Goals Available For Lead',
    type: 'boolean',
    props: {
      defaultChecked: false,
    },
  },
  {
    id: 'teamSelfEvaluation',
    name: 'Team Self Evaluation',
    type: 'boolean',
    props: {
      defaultChecked: false,
    },
  },
  {
    id: 'icrObjection',
    name: 'ICR Objection',
    type: 'boolean',
    props: {
      defaultChecked: false,
    },
  },
  {
    id: 'notification',
    name: 'Notification',
    type: {
      addText: 'Add Notification',
      array: {
        id: '<auto>',
        name: 'notification',
        type: {
          object: [
            {
              id: 'title',
              type: 'text',
              props: {
                showCount: true,
                maxLength: 50,
                placeholder: 'Please write notification title',
              },
              options: {
                rules: [
                  { required: true, whitespace: true, message: 'Notification Title required' },
                ],
              },
            },
            {
              id: 'notification',
              type: 'text',
              props: {
                showCount: true,
                maxLength: 200,
                placeholder: 'Please write notification',
              },
              options: {
                rules: [{ required: true, whitespace: true, message: 'Notification MSG required' }],
              },
            },
            {
              id: 'date',
              type: 'date',
            },
          ],
        },
      },
    },
  },
];

const schema1 = [
  {
    id: 'name',
    name: 'Name',
    type: 'string',
    options: {
      rules: [{ required: true, whitespace: true, message: 'Name required' }],
    },
  },
  {
    id: 'duration',
    name: 'Duration',
    type: 'dateRange',
    options: {
      rules: [{ required: true, message: 'Duration required' }],
    },
    layout: { span: 24 },
    props: { showTime: false, disabled: false },
  },
  {
    id: 'status',
    name: 'Status',
    type: 'select',
    props: { values: ['Active', 'Completed', 'Non Active', 'Cancelled'] },
  },
  {
    id: 'teamGoalAvailableForLead',
    name: 'Team Goals Available For Lead',
    type: 'boolean',
  },
  {
    id: 'teamSelfEvaluation',
    name: 'Team Self Evaluation',
    type: 'boolean',
  },
  {
    id: 'icrObjection',
    name: 'ICR Objection',
    type: 'boolean',
  },
  {
    id: 'notification',
    name: 'Notification',
    type: {
      addText: 'Add Notification',
      array: {
        id: '<auto>',
        name: 'notification',
        type: {
          object: [
            {
              id: 'title',
              type: 'text',
              props: {
                showCount: true,
                maxLength: 50,
                placeholder: 'Please write notification title',
              },
              options: {
                rules: [
                  { required: true, whitespace: true, message: 'Notification Title required' },
                ],
              },
            },
            {
              id: 'notification',
              type: 'text',
              props: {
                showCount: true,
                maxLength: 200,
                placeholder: 'Please write notification',
              },
              options: {
                rules: [{ required: true, whitespace: true, message: 'Notification MSG required' }],
              },
            },
            {
              id: 'date',
              type: 'date',
            },
          ],
        },
      },
    },
  },
];

const PerformanceCyclePage: FC = () => {
  const [visible, setVisible] = useState<boolean>(false);
  const [filter, setFilter] = useState<any>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [data, setData] = useState<PerformanceCycle[]>();
  const [total, setTotal] = useState<number>(0);
  const [current, setCurrent] = useState<PerformanceCycle>();
  const [pagination, setPagination] = useState<DataTablePagination>({ page: 1, size: 10 });
  const connection = useConnection();
  const createForm = useFormModal<PerformanceCycle>();
  const editForm = useFormModal<PerformanceCycle>();

  //Test for API Endpoint to send notifications for all users , you may remove this comment & useEffect block
  // useEffect(() => {
  //   connection.get('cycles/notifications', { time: 22 }).then((response) => {
  //     console.log('response', response);
  //   });
  // }, [connection]);

  const getCycle = useCallback(
    (filter: any, pagination: DataTablePagination) => {
      setLoading(true);
      connection
        .get('cycles', { ...pagination, ...filter })
        .then((response: { total: number; performanceCycle: PerformanceCycle[] }) => {
          setData(response.performanceCycle.map(mapCycle) as any);
          setTotal(response.total);
        })
        .finally(() => setLoading(false));
    },
    [connection],
  );

  useEffect(() => {
    getCycle({}, { page: 1, size: 10 });
  }, [connection, getCycle]);

  const addCycle = useCallback(
    async (performanceCycle: PerformanceCycle) => {
      try {
        const data = { ...performanceCycle };
        data.startDate = (data.duration && data.duration[0]) ?? '';
        data.endDate = (data.duration && data.duration[1]) ?? '';
        data.teamGoalAvailableForLead = data.teamGoalAvailableForLead ?? false;
        data.teamSelfEvaluation = data.teamSelfEvaluation ?? false;
        data.icrObjection = data.icrObjection ?? false;
        delete data.duration;
        await connection.post('cycles', data);
        createForm.close();
        getCycle(filter, pagination);
      } catch (e: any) {
        if (e.body?.body?.errors) {
          createForm.setErrors(e.body.body.errors);
        } else {
          createForm.setErrors([]);
        }
      }
    },
    [connection, createForm, filter, getCycle, pagination],
  );

  const editCycle = useCallback(
    async (performanceCycle: PerformanceCycle) => {
      try {
        const data = { ...performanceCycle };
        data.startDate = (data.duration && data.duration[0]) ?? '';
        data.endDate = (data.duration && data.duration[1]) ?? '';
        data.teamGoalAvailableForLead = data.teamGoalAvailableForLead ?? false;
        data.teamSelfEvaluation = data.teamSelfEvaluation ?? false;
        data.icrObjection = data.icrObjection ?? false;
        delete data.duration;

        const performanceCycleId = current && current.id;
        if (performanceCycleId) {
          performanceCycle.id = performanceCycleId;
        }
        await connection.patch(`cycles/${performanceCycleId}`, data);
        editForm.close();
        setVisible(false);
        getCycle(filter, pagination);
      } catch (e: any) {
        if (e.body?.body?.errors) {
          editForm.setErrors(e.body.body.errors);
        } else {
          editForm.setErrors([]);
        }
      }
    },
    [connection, current, editForm, filter, getCycle, pagination],
  );

  const search = useCallback(
    (filter: any, pagination: DataTablePagination) => {
      setLoading(true);
      connection
        .get('cycles', { ...pagination, ...filter })
        .then((response) => {
          setData(response.performanceCycle.map(mapCycle));
          setTotal(response.total);
        })
        .finally(() => setLoading(false));
    },
    [connection],
  );

  const deleteCycle = useCallback(() => {
    const performanceCycleId = current && current.id;
    Modal.confirm({
      title: 'Delete Performance Cycle',
      icon: <ExclamationCircleOutlined />,
      content: 'Are you sure delete to this Performance Cycle?',
      okText: 'Delete',
      okType: 'danger',
      onOk() {
        connection
          .delete(`cycles/${performanceCycleId}`)
          .then(() => {
            setVisible(false);
            getCycle(filter, pagination);
            message.success('Cycle deleted');
          })
          .catch(() => {
            message.error('Cycle might have goals');
          });
      },
      onCancel() {
        console.log('Cancel');
      },
    });
  }, [connection, current, filter, getCycle, pagination]);

  return (
    <WebUxPage>
      <PageHeader
        ghost={false}
        style={{ background: '#FFF' }}
        title="Performance Cycle"
        subTitle="Performance cycle management"
      />
      <WebUxPageContent>
        <WebUxPageItem
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <QueryBuilder
            searchPlaceholder="Search Performance Cycle"
            search={true}
            value={filter}
            onChange={setFilter}
            onSearch={(value) => {
              setPagination({ page: 1, size: 10 });
              search(value, pagination);
            }}
            schema={[]}
          />
          <Space>
            <Button
              onClick={() => {
                setFilter({});
                setPagination({ page: 1, size: 10 });
                search({}, { page: 1, size: 10 });
              }}>
              Reset
            </Button>
            <Button type="primary">Search</Button>
          </Space>
        </WebUxPageItem>
        <WebUxPageItem>
          <Space direction={'vertical'} style={{ width: '100%' }} size={16}>
            <FlexBox horizontalAlign={'right'}>
              <Button
                icon={<PlusOutlined />}
                key="1"
                type="primary"
                onClick={() => createForm.open()}>
                New Performance Cycle
              </Button>
            </FlexBox>
            <DataTable
              columns={columns}
              total={total}
              loading={loading}
              dataSource={data}
              onPaginationChange={(p) => {
                setPagination(p);
                if (pagination.page !== p.page || pagination.size !== p.size) {
                  search(filter, p);
                }
              }}
              onClick={(item: PerformanceCycle) => {
                setVisible(true);
                setCurrent(item);
              }}
              dtPagination={pagination}
            />
          </Space>
        </WebUxPageItem>
      </WebUxPageContent>
      <Modal
        open={visible}
        title={'Performance Cycle'}
        onCancel={() => {
          setVisible(false);
        }}
        footer={
          <Space align={'end'}>
            <Button
              onClick={() => {
                setVisible(false);
              }}>
              Close
            </Button>
            <Button
              onClick={() => {
                console.log(current);
                editForm.open(current);
              }}>
              Edit
            </Button>
            <Button type="primary" onClick={deleteCycle}>
              Delete
            </Button>
          </Space>
        }>
        <InfoView>
          <InfoViewItem title={'Name'}>{current?.name}</InfoViewItem>
          <InfoViewItem title={'Status'}>{current?.status}</InfoViewItem>
          <InfoViewItem title={'Start Date'}>{current?.startDate}</InfoViewItem>
          <InfoViewItem title={'End Date'}>{current?.endDate}</InfoViewItem>
          <InfoViewItem title={'Team Goals Available For Lead'}>
            {current?.teamGoalAvailableForLead ? 'Enabled' : 'Disabled'}
          </InfoViewItem>
          <InfoViewItem title={'Team Self Evaluation'}>
            {current?.teamSelfEvaluation ? 'Enabled' : 'Disabled'}
          </InfoViewItem>
          <InfoViewItem title={'ICR Objection'}>
            {current?.icrObjection ? 'Enabled' : 'Disabled'}
          </InfoViewItem>
        </InfoView>
        {current?.notification && current?.notification.length > 0 && (
          <DataTable
            columns={notificationColumns}
            total={current?.notification ? current?.notification.length : 0}
            loading={loading && current?.notification ? current?.notification.length > 0 : false}
            dataSource={current?.notification}
          />
        )}
      </Modal>
      <FormModal title={'Add Performance Cycle'} onSubmit={addCycle} form={createForm}>
        <Form.Items schema={schema} />
      </FormModal>
      <FormModal title={'Edit Performance Cycle'} onSubmit={editCycle} form={editForm}>
        <Form.Items schema={schema1} />
      </FormModal>
    </WebUxPage>
  );
};
export default PerformanceCyclePage;
